import React from "react";
import { TATVA_PHOTO_WYNN_B64 } from "../lib/tatvaPhotoWynn";

/**
 * Wynn Al Marjan Island — Production Manager · Audio Visual
 * Job ID 1157 · Apply by 13 July 2026
 *
 * One-page A4 cover letter — courteous, factual, JD-aligned. No flourish.
 *
 * Exports:
 *   - WynnCoverSheet (named): used in /application-wynn
 *   - WynnCoverLetter (default): standalone /cover-letter-wynn page
 */

const CREAM = "#f4ede0";
const INK = "#1a1a1a";
const INK_SOFT = "#3a342b";
const BRONZE = "#8a6f47";
const RULE_SOFT = "rgba(26,26,26,0.10)";

const SERIF = '"Playfair Display", Georgia, "Times New Roman", serif';
const SANS = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";

const Eyebrow = ({ children }) => (
    <p
        style={{
            fontFamily: SANS,
            color: BRONZE,
            fontSize: 9,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            fontWeight: 600,
            margin: 0,
        }}
    >
        {children}
    </p>
);

export const WynnCoverSheet = () => (
    <article
        className="cover-page"
        data-testid="cover-letter-sheet"
        style={{
            width: 794,
            height: 1120,
            maxWidth: "100%",
            margin: "0 auto",
            backgroundColor: CREAM,
            color: INK,
            boxShadow: "0 24px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(26,26,26,0.06)",
            padding: "56px 80px 44px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: SANS,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}
    >
        <header
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 22,
                paddingBottom: 18,
                borderBottom: `1px solid ${RULE_SOFT}`,
                marginBottom: 26,
            }}
        >
            <div style={{ flex: 1 }}>
                <h1
                    style={{
                        fontFamily: SERIF,
                        fontSize: 32,
                        fontWeight: 500,
                        color: INK,
                        margin: 0,
                        letterSpacing: "-0.02em",
                        lineHeight: 1,
                    }}
                >
                    Aditya Anand
                </h1>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        fontSize: 12,
                        color: BRONZE,
                        margin: "4px 0 0",
                    }}
                >
                    Known professionally as Tatva K
                </p>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        fontSize: 12,
                        color: INK_SOFT,
                        margin: "6px 0 0",
                    }}
                >
                    Live AV Production &middot; Sound Engineer &middot; Broadcast Producer
                </p>
                <p
                    style={{
                        fontFamily: SANS,
                        fontSize: 9.5,
                        color: INK_SOFT,
                        margin: "10px 0 0",
                        letterSpacing: "0.04em",
                        lineHeight: 1.55,
                    }}
                >
                    Al Hamra Village, Ras Al Khaimah, UAE
                    <br />
                    tatvak@theresetsanctuary.org &middot; +971 50 676 9007
                </p>
            </div>
            <img
                src={TATVA_PHOTO_WYNN_B64}
                alt="Tatva K"
                style={{
                    width: 92,
                    height: 110,
                    objectFit: "cover",
                    border: `1px solid rgba(26,26,26,0.18)`,
                    flexShrink: 0,
                }}
            />
        </header>

        <div
            style={{
                fontFamily: SANS,
                fontSize: 9.5,
                color: BRONZE,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 600,
                margin: "0 0 22px",
            }}
        >
            Re: Production Manager &mdash; Audio Visual &middot; Job ID 1157
        </div>

        <section
            style={{
                fontFamily: SERIF,
                fontSize: 12,
                lineHeight: 1.6,
                color: INK_SOFT,
                fontWeight: 400,
            }}
        >
            <p style={{ margin: "0 0 11px" }}>Dear hiring team,</p>

            <p style={{ margin: "0 0 11px" }}>
                I am writing to apply for the Production Manager &mdash; Audio Visual
                position at Wynn Al Marjan Island. I am a UAE resident based in{" "}
                <strong style={{ color: INK }}>Al Hamra Village, Ras Al Khaimah</strong>,
                available on-site immediately and prepared to commit fully to the
                pre-opening team.
            </p>

            <p style={{ margin: "0 0 11px" }}>
                I bring twenty years across live performance, broadcast and sound
                engineering. I performed on the{" "}
                <strong style={{ color: INK }}>
                    Sunburn Festival mainstage from 2006 through 2009
                </strong>{" "}
                alongside Armin van Buuren and Defected Records artists, and across the
                wider Indian club and festival circuit including Plethora, Elevate, MTV
                Flyp Delhi, and Playboy Club. I have produced high-end private events at
                Mehrangarh Fort and Udaipur Palace, and many destination weddings for
                Indian HNWI clientele. The live AV environments your role describes are
                ones I have worked inside for two decades.
            </p>

            <p style={{ margin: "0 0 11px" }}>
                For thirteen years I was{" "}
                <strong style={{ color: INK }}>
                    Producer, DJ and RJ for &lsquo;Mocktail&rsquo; on Zabardast Hit
                    95&nbsp;FM
                </strong>{" "}
                &mdash; a national flagship radio show. That role taught me daily
                broadcast uptime, live-to-air signal discipline, talent and guest
                coordination, and how mission-critical AV operations are run without
                error.
            </p>

            <p style={{ margin: "0 0 11px" }}>
                On the engineering side, I am a{" "}
                <strong style={{ color: INK }}>Berklee-trained</strong> sound engineer,
                an Ableton Live user since 2004, and have engineered and mastered
                commercial releases totalling over <strong style={{ color: INK }}>200 million streams and views</strong>.
                My studio chain is Antelope Audio Orion 32 conversion, SSL X-Rack
                preamps, TLA Ivory, SPL 5.1, and TC Electronic Finalizer for mastering.
            </p>

            <p style={{ margin: "0 0 11px" }}>
                I have led teams as Founder and Managing Director of Chill Om Records &amp;
                Studios, and currently operate The Reset Sanctuary in the UAE. I am
                comfortable owning AV equipment lifecycle, vendor relationships, team
                training, and the daily quality control your job description outlines.
            </p>

            <p style={{ margin: "0 0 11px" }}>
                I would welcome the opportunity to discuss how my experience can serve
                the AV and Entertainment Technical Services team at Wynn Al Marjan
                Island. I am happy to meet on-site at your convenience.
            </p>

            <p style={{ margin: 0 }}>Thank you for your time and consideration.</p>
        </section>

        <div
            style={{
                marginTop: "auto",
                paddingTop: 22,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
            }}
        >
            <div>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        fontSize: 22,
                        color: INK,
                        margin: 0,
                        letterSpacing: "-0.01em",
                        lineHeight: 1,
                    }}
                >
                    Warm regards,
                </p>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontSize: 20,
                        color: BRONZE,
                        margin: "8px 0 0",
                        fontWeight: 500,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Aditya Anand
                </p>
            </div>
            <div style={{ textAlign: "right" }}>
                <Eyebrow>Job ID 1157 &middot; Apply Before 13 July 2026</Eyebrow>
                <p
                    style={{
                        fontFamily: SANS,
                        fontSize: 9.5,
                        color: INK_SOFT,
                        margin: "8px 0 0",
                        letterSpacing: "0.04em",
                        lineHeight: 1.6,
                    }}
                >
                    UAE Resident &middot; On-site Ready
                </p>
            </div>
        </div>
    </article>
);

const WynnCoverLetter = () => (
    <main
        data-testid="cover-letter-wynn-page"
        style={{
            backgroundColor: "#2a2620",
            minHeight: "100vh",
            padding: "40px 16px 80px",
            fontFamily: SANS,
        }}
    >
        <div
            className="cover-toolbar"
            style={{
                maxWidth: 794,
                margin: "0 auto 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
            }}
        >
            <p
                style={{
                    color: "#c9a227",
                    fontSize: 11,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    margin: 0,
                    fontWeight: 600,
                }}
            >
                Tatva K · Wynn Cover Letter · Job ID 1157
            </p>
        </div>
        <WynnCoverSheet />
    </main>
);

export default WynnCoverLetter;
